import { EvidenceCardConfig } from "@risalabs_frontend_org/oasis-ui-kit";
import {
  Answer,
  Choice,
  CmmMedicalNeccessityQuestionnaireForUI,
  PharmaBranchedQuestionnaireDocument,
  Question,
  QuestionForUI,
  QuestionOption,
  QuestionType,
  QUESTION_TYPE,
  UserFeedback,
} from "../data-model/cmmMedicalNeccessityQuestionnaire";

const EvidenceCardBackgroundColor = {
  SUPPORTIVE: "#E6F3F0",
  CONTRADICTORY: "#FFE8E8",
  NEUTRAL: "#F5F5F5",
} as const;

/**
 * Converts a single raw `Question` (the shape stored in the CMM workflow
 * Firestore document) into the flattened `QuestionForUI` shape that the
 * questionnaire renderer consumes.
 *
 * Field-by-field mapping:
 *   - `question`                    ← `Question.text`
 *   - `question_type`               ← derived:
 *       • no choices                → "subjective"     (free-text only)
 *       • choices + array answer    → "multi-subjective" (checkbox group)
 *       • choices + scalar answer   → "radio-subjective" (radio group)
 *   - `options`                     ← `choices.map(c => { label: c.text, value: c.name })`
 *   - `show_optional_free_text_for` ← names of choices flagged `additional_free_text === "O"`
 *   - `show_mandatory_free_text_for`← names of choices flagged `additional_free_text === "M"`
 *   - `answer`                      ← `Question.answer` (`null` coerced to `""`)
 *   - `free_text_answer`            ← first choice carrying a non-empty
 *                                     `additional_free_text_answer`, returned as
 *                                     `{ answer, name }`
 *   - `name`                        ← `Question.name` (`null` coerced to `""`)
 *   - `next_question_name`          ← navigation hint (choice-level wins so
 *                                     answers can branch independently of the
 *                                     question's default successor):
 *       1. If the question is answered, look up the choice whose text matches
 *          a selected answer and that has a `next_question`; if found, use it.
 *       2. Otherwise, if `Question.next_question` is set, use it as the
 *          default successor.
 *       3. Otherwise, `""` (terminal / unanswered).
 *   - `shouldShowTextArea`          ← `true` when at least one of the currently
 *                                     selected answer values appears in either
 *                                     `show_optional_free_text_for` or
 *                                     `show_mandatory_free_text_for`. `false`
 *                                     when unanswered or when no selected
 *                                     choice requires/offers a free-text field.
 *   - `isTextAreaMandatory`         ← `true` when at least one of the currently
 *                                     selected answer values is listed in
 *                                     `show_mandatory_free_text_for`. Handles
 *                                     both single-string and multi-select
 *                                     answers; `false` when unanswered.
 */
export function transformQuestionToQuestionForUI(
  question: Question,
): QuestionForUI {
  const choices = question.choices ?? [];
  const hasChoices = choices.length > 0;

  let question_type: QuestionType;
  if (!hasChoices) {
    question_type = QUESTION_TYPE.SUBJECTIVE;
  } else if (Array.isArray(question.answer)) {
    question_type = QUESTION_TYPE.MULTI_SUBJECTIVE;
  } else {
    question_type = QUESTION_TYPE.RADIO_SUBJECTIVE;
  }

  const options: QuestionOption[] = choices.map((choice) => ({
    label: choice.text,
    value: choice.text,
  }));

  const show_optional_free_text_for = choices
    .filter((choice) => choice.additional_free_text === "O")
    .map((choice) => choice.text);

  const show_mandatory_free_text_for = choices
    .filter((choice) => choice.additional_free_text === "M")
    .map((choice) => choice.text);

  const answer: string | string[] = question.answer ?? "";

  const freeTextChoice = choices.find(
    (choice) =>
      choice.additional_free_text_answer != null &&
      choice.additional_free_text_answer !== "",
  );
  const free_text_answer = {
    answer: freeTextChoice?.additional_free_text_answer ?? "",
    name: freeTextChoice?.name ?? "",
  };

  const defaultNextQuestion = question.next_question?.trim() ?? "";
  let next_question_name = defaultNextQuestion;
  const hasMeaningfulAnswer =
    question.answer != null &&
    (Array.isArray(question.answer)
      ? question.answer.length > 0
      : question.answer.trim() !== "");
  if (hasMeaningfulAnswer) {
    const answeredNames = Array.isArray(question.answer)
      ? question.answer
      : [question.answer as string];
    const matched = choices.find(
      (choice) =>
        answeredNames.includes(choice.text) &&
        choice.next_question &&
        choice.next_question.trim() !== "",
    );
    if (matched?.next_question) {
      next_question_name = matched.next_question;
    }
  } else {
    next_question_name = "";
  }

  const answeredNames = Array.isArray(question.answer)
    ? question.answer
    : question.answer != null
      ? [question.answer]
      : [];

  const shouldShowTextArea = answeredNames.some(
    (a) =>
      show_optional_free_text_for.includes(a) ||
      show_mandatory_free_text_for.includes(a),
  );

  const isTextAreaMandatory = answeredNames.some((a) =>
    show_mandatory_free_text_for.includes(a),
  );

  const supportiveFacts: EvidenceCardConfig = {
    title: "Supportive Facts",
    statements: question?.facts?.supportive ?? [],
    cardBackGroundColor: EvidenceCardBackgroundColor.SUPPORTIVE,
  };

  const contradictoryFacts: EvidenceCardConfig = {
    title: "Contradictory Facts",
    statements: question?.facts?.contradictory ?? [],
    cardBackGroundColor: EvidenceCardBackgroundColor.CONTRADICTORY,
  };

  const thinkingStatement = question?.facts?.thinking;
  const thinkingFacts: EvidenceCardConfig = {
    title: "Thinking",
    statements: thinkingStatement ? [thinkingStatement] : [],
    cardBackGroundColor: EvidenceCardBackgroundColor.NEUTRAL,
  };

  const evidenceCards: EvidenceCardConfig[] = [
    supportiveFacts,
    contradictoryFacts,
    thinkingFacts,
  ].filter((card) => (card.statements?.length ?? 0) > 0);

  const confidenceScore = (question?.confidence_score ?? 0) * 100;
  let finalAnswerChangedReason = "";
  const allUserfeedbackComments = question?.user_feedback ?? [];
  if (allUserfeedbackComments.length > 0) {
    const latestUserfeedbackComment = allUserfeedbackComments.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )[0];
    finalAnswerChangedReason = `${latestUserfeedbackComment.selected_option} - ${latestUserfeedbackComment.comment}.`;
  }

  return {
    question: question.text,
    question_type,
    show_optional_free_text_for,
    show_mandatory_free_text_for,
    answer,
    free_text_answer,
    name: question.name ?? "",
    next_question_name,
    options,
    shouldShowTextArea,
    isTextAreaMandatory,
    evidenceCards,
    confidenceScore,
    answerChangedReason:
      finalAnswerChangedReason.trim().length > 0
        ? finalAnswerChangedReason
        : undefined,
  };
}

/**
 * Adapter that takes a full CMM workflow Firestore document and produces the
 * UI-ready questionnaire payload.
 *
 * Behavior:
 *   - Reads `doc.cmm_data_latest.clinical_question_sections[0].questions` —
 *     i.e. only the first clinical section is considered today. Other sections
 *     (`special_question_sections`, `medical_details_questions`) are ignored.
 *   - Defensively defaults to an empty list when the path is missing, so the
 *     function never throws on partially-populated documents.
 *   - Each raw `Question` is delegated to {@link transformQuestionToQuestionForUI}
 *     for the heavy lifting; this function is purely a section selector + mapper.
 *
 * @param doc Raw CMM workflow document (as fetched from Firestore).
 * @returns `{ questions: QuestionForUI[] }` ready to bind to the renderer.
 */
export function transformCmmWorkflowToQuestionnaireForUI(
  doc: PharmaBranchedQuestionnaireDocument,
): CmmMedicalNeccessityQuestionnaireForUI {
  const firstSection = doc.cmm_data_latest?.clinical_question_sections?.[0];
  const rawQuestions = firstSection?.questions ?? [];

  return {
    questions: rawQuestions.map(transformQuestionToQuestionForUI),
  };
}

/**
 * Patch describing a single edit to apply to the first clinical question
 * section of a CMM workflow document.
 *
 * Two shapes are supported:
 *   1. Answer patch — `{ name, answer }`: locate the question by `name` and
 *      replace its `answer` field.
 *   2. Free-text patch — `{ name, choiceText, freeText }`: locate the question
 *      by `name`, then locate its choice by `text` (matched case-sensitively),
 *      and replace that choice's `additional_free_text_answer`.
 */
export type CmmWorkflowQuestionPatch =
  | { name: string; answer: Answer }
  | { name: string; choiceText: string; freeText: string };

/**
 * Result of applying a patch via {@link applyPatchToCmmWorkflow}.
 *
 * - `document` is a new {@link PharmaBranchedQuestionnaireDocument} with the first clinical
 *   question section updated (the input doc is not mutated).
 * - `questions` is the re-transformed UI-ready questionnaire derived from the
 *   new document, ready to feed back into the renderer.
 */
export interface CmmWorkflowPatchResult {
  document: PharmaBranchedQuestionnaireDocument;
  questions: QuestionForUI[];
}

/**
 * Immutably apply a {@link CmmWorkflowQuestionPatch} to the first clinical
 * question section of a CMM workflow document and return the updated document
 * along with the re-transformed UI questions.
 *
 * Behavior:
 *   - Only the first entry of `cmm_data_latest.clinical_question_sections` is
 *     considered (mirrors {@link transformCmmWorkflowToQuestionnaireForUI}).
 *   - The patched question is located by `name` in the live `doc`. Its
 *     `answer` is replaced (answer patch), or its matching choice's
 *     `additional_free_text_answer` is replaced (free-text patch).
 *   - If the target question cannot be found, the document is returned
 *     unchanged (the questions are still re-transformed for consistency).
 *   - The input `doc` is never mutated; nested arrays/objects along the
 *     update path are shallow-cloned.
 *
 * @param doc    The current workflow document.
 * @param patch  The patch to apply.
 */
export function applyPatchToCmmWorkflow(
  doc: PharmaBranchedQuestionnaireDocument,
  patch: CmmWorkflowQuestionPatch,
): CmmWorkflowPatchResult {
  const sections = doc.cmm_data_latest?.clinical_question_sections ?? [];
  const firstSection = sections[0];

  if (!firstSection) {
    return {
      document: doc,
      questions: transformCmmWorkflowToQuestionnaireForUI(doc).questions,
    };
  }

  const matchedIndex = firstSection.questions.findIndex(
    (q) => q.name === patch.name,
  );

  if (matchedIndex === -1) {
    return {
      document: doc,
      questions: transformCmmWorkflowToQuestionnaireForUI(doc).questions,
    };
  }

  const matched = firstSection.questions[matchedIndex];

  let patchedQuestion: Question;
  if ("answer" in patch) {
    patchedQuestion = { ...matched, answer: patch.answer };
  } else {
    const choices = matched.choices ?? [];
    const updatedChoices: Choice[] = choices.map((choice) =>
      choice.text === patch.choiceText || choice.name === patch.choiceText
        ? { ...choice, additional_free_text_answer: patch.freeText }
        : choice,
    );
    patchedQuestion = { ...matched, choices: updatedChoices };
  }

  const updatedQuestions: Question[] = [
    ...firstSection.questions.slice(0, matchedIndex),
    patchedQuestion,
    ...firstSection.questions.slice(matchedIndex + 1),
  ];

  const updatedSection = { ...firstSection, questions: updatedQuestions };
  const updatedSections = [updatedSection, ...sections.slice(1)];

  const updatedDoc: PharmaBranchedQuestionnaireDocument = {
    ...doc,
    cmm_data_latest: {
      ...doc.cmm_data_latest,
      clinical_question_sections: updatedSections,
    },
  };

  return {
    document: updatedDoc,
    questions: transformCmmWorkflowToQuestionnaireForUI(updatedDoc).questions,
  };
}

/**
 * Immutably appends a {@link UserFeedback} entry to the matching question's
 * `user_feedback` array in the first clinical question section of a workflow
 * document. Pure structural edit — no answer/choice/free-text logic, no
 * downstream resets, no re-transformation.
 *
 * Behavior:
 *   - Only the first entry of `cmm_data_latest.clinical_question_sections` is
 *     considered (mirrors {@link applyPatchToCmmWorkflow}).
 *   - The target question is located by `name`. If it cannot be found, the
 *     document is returned unchanged.
 *   - When the question's existing `user_feedback` is missing, it's treated
 *     as an empty array and replaced with `[feedback]`.
 *   - The input `doc` is never mutated; nested arrays/objects along the
 *     update path are shallow-cloned.
 *
 * @param doc       The current workflow document.
 * @param name      The `Question.name` of the question receiving the feedback.
 * @param feedback  A single {@link UserFeedback} entry to append.
 * @returns         A new workflow document with the entry appended, or the
 *                  original `doc` reference when no edit was applied.
 */
export function appendUserFeedbackToCmmWorkflow(
  doc: PharmaBranchedQuestionnaireDocument,
  name: string,
  feedback: UserFeedback,
): PharmaBranchedQuestionnaireDocument {
  const sections = doc.cmm_data_latest?.clinical_question_sections ?? [];
  const firstSection = sections[0];
  if (!firstSection) return doc;

  const matchedIndex = firstSection.questions.findIndex((q) => q.name === name);
  if (matchedIndex === -1) return doc;

  const matched = firstSection.questions[matchedIndex];
  const existingFeedback = matched.user_feedback ?? [];
  const updatedQuestion: Question = {
    ...matched,
    user_feedback: [...existingFeedback, feedback],
  };

  const updatedQuestions: Question[] = [
    ...firstSection.questions.slice(0, matchedIndex),
    updatedQuestion,
    ...firstSection.questions.slice(matchedIndex + 1),
  ];

  const updatedSection = { ...firstSection, questions: updatedQuestions };
  const updatedSections = [updatedSection, ...sections.slice(1)];

  return {
    ...doc,
    cmm_data_latest: {
      ...doc.cmm_data_latest,
      clinical_question_sections: updatedSections,
    },
  };
}

/**
 * Immutably clears the entire `user_feedback` array on the matching question
 * in the first clinical question section of a workflow document. Pure
 * structural edit — companion to {@link appendUserFeedbackToCmmWorkflow}.
 *
 * Behavior:
 *   - Only the first entry of `cmm_data_latest.clinical_question_sections` is
 *     considered.
 *   - The target question is located by `name`. If it cannot be found, the
 *     document is returned unchanged.
 *   - When the question's `user_feedback` is missing or already empty, the
 *     document is returned unchanged (no-op).
 *   - The input `doc` is never mutated; nested arrays/objects along the
 *     update path are shallow-cloned.
 *
 * @param doc   The current workflow document.
 * @param name  The `Question.name` of the question whose feedback to clear.
 * @returns     A new workflow document with the question's `user_feedback`
 *              set to `[]`, or the original `doc` reference when no edit was
 *              applied.
 */
export function clearUserFeedbackFromCmmWorkflow(
  doc: PharmaBranchedQuestionnaireDocument,
  name: string,
): PharmaBranchedQuestionnaireDocument {
  const sections = doc.cmm_data_latest?.clinical_question_sections ?? [];
  const firstSection = sections[0];
  if (!firstSection) return doc;

  const matchedIndex = firstSection.questions.findIndex((q) => q.name === name);
  if (matchedIndex === -1) return doc;

  const matched = firstSection.questions[matchedIndex];
  const existingFeedback = matched.user_feedback ?? [];
  if (existingFeedback.length === 0) return doc;

  const updatedQuestion: Question = {
    ...matched,
    user_feedback: [],
  };

  const updatedQuestions: Question[] = [
    ...firstSection.questions.slice(0, matchedIndex),
    updatedQuestion,
    ...firstSection.questions.slice(matchedIndex + 1),
  ];

  const updatedSection = { ...firstSection, questions: updatedQuestions };
  const updatedSections = [updatedSection, ...sections.slice(1)];

  return {
    ...doc,
    cmm_data_latest: {
      ...doc.cmm_data_latest,
      clinical_question_sections: updatedSections,
    },
  };
}
